import { type ReactNode } from 'react';

interface LayoutProps {
  title: string;
  /** Rendered before the title — a project's mark, so a page says whose it is before it says what. */
  leading?: ReactNode;
  subtitle?: string;
  action?: ReactNode;
  children: ReactNode;
  fullWidth?: boolean;
  /**
   * Tighter column than the default. For pages that are a single stack of cards rather than a
   * table — reading a column of short rows across the full width is the harder job.
   */
  narrow?: boolean;
}

export function Layout({ title, leading, subtitle, action, children, fullWidth, narrow }: LayoutProps) {
  return (
    <div className="min-h-screen bg-surface">
      {/* No <Sidebar/> here: the app chrome is mounted once by AppShell above the routes, so it
          survives navigation instead of remounting with every page. */}
      <main className="pl-[var(--sidebar-w)] pr-[var(--rail-w)] min-h-screen">
        <div
          className={
            fullWidth ? 'px-6 py-8' : `${narrow ? 'max-w-4xl' : 'max-w-6xl'} mx-auto px-8 py-8`
          }
        >
          {/* Page header */}
          <div className="flex items-start justify-between gap-6 mb-8">
            {/* min-w-0 so a long subtitle wraps inside this column instead of pushing the action
                out of the row, and shrink-0 on the action so it keeps its natural width. */}
            <div className="min-w-0">
              {/* text-lg, matching the pages that draw their own header (KB review, Ask, Settings…)
                  — this component is used by four pages and they must not read as a different app. */}
              <h1 className="text-lg font-bold text-ink flex items-center gap-2">
                {leading && <span className="flex-shrink-0">{leading}</span>}
                <span className="min-w-0">{title}</span>
              </h1>
              {subtitle && <p className="mt-1 text-xs text-ink-muted max-w-2xl">{subtitle}</p>}
            </div>
            {action && <div className="flex-shrink-0">{action}</div>}
          </div>
          {children}
        </div>
      </main>
    </div>
  );
}
